/**
 * Builds multi-line /cart/add.js payloads for main product + installation hardware add-ons.
 * Returns null when no grouped add-on is selected so callers can use the default single-item flow.
 */
(function () {
  const PROPERTY_PREFIX = 'properties[';
  const PROPERTY_SUFFIX = ']';

  function createGroupId() {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }

    return 'kg-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
  }

  function extractLineItemProperties(formData) {
    const properties = {};

    formData.forEach(function (value, key) {
      if (!key.startsWith(PROPERTY_PREFIX) || !key.endsWith(PROPERTY_SUFFIX)) return;

      const name = key.slice(PROPERTY_PREFIX.length, -PROPERTY_SUFFIX.length);
      if (value !== null && value !== undefined && String(value).trim() !== '') {
        properties[name] = String(value);
      }
    });

    return properties;
  }

  function getAddonVariantId(form) {
    if (!form) return null;

    const formData = new FormData(form);
    const variantId = formData.get('properties[_installation_hardware_variant_id]');
    if (!variantId || String(variantId).trim() === '') return null;

    const hardware = formData.get('properties[Installation Hardware]');
    if (!hardware || String(hardware).trim() === '' || String(hardware).trim() === 'None') return null;

    return String(variantId).trim();
  }

  function hasGroupedAddons(form) {
    return !!getAddonVariantId(form);
  }

  function findMainCartLineItem(items, mainVariantId) {
    if (!Array.isArray(items) || !items.length) return null;

    const mainVariantIdStr = String(mainVariantId);

    return (
      items.find(function (item) {
        return item.properties && item.properties._group_role === 'main';
      }) ||
      items.find(function (item) {
        return String(item.variant_id || item.id) === mainVariantIdStr;
      }) ||
      items[0]
    );
  }

  /**
   * Multi-item /cart/add.js responses use { items: [...] } without a top-level key.
   * Dawn cart UI expects single-line-item shape with key/id for section rendering.
   */
  function normalizeCartAddResponse(response, mainVariantId) {
    if (!response || response.status) return response;
    if (response.key) return response;

    const items = Array.isArray(response.items) ? response.items : null;
    if (!items || !items.length) return response;

    const mainItem = findMainCartLineItem(items, mainVariantId);
    if (!mainItem || !mainItem.key) return response;

    return Object.assign({}, response, {
      id: mainItem.product_id || mainItem.id,
      key: mainItem.key,
      variant_id: mainItem.variant_id || mainItem.id,
    });
  }

  function getLineItemProperty(item, name) {
    if (!item || !item.properties) return '';

    const value = item.properties[name];
    return value != null ? String(value) : '';
  }

  /**
   * When removing a main grouped line, also remove its add-ons via /cart/update.js.
   * Returns null for normal single-line removal.
   */
  function getGroupedRemovalUpdates(cart, lineIndex) {
    const line = parseInt(lineIndex, 10);
    if (!cart || !Array.isArray(cart.items) || Number.isNaN(line) || line < 1) return null;

    const targetItem = cart.items[line - 1];
    if (!targetItem || !targetItem.key) return null;

    const groupId = getLineItemProperty(targetItem, '_group_product');
    if (!groupId) return null;

    const updates = {};
    let groupedItemCount = 0;

    cart.items.forEach(function (item) {
      if (!item || !item.key) return;
      if (getLineItemProperty(item, '_group_product') !== groupId) return;

      updates[item.key] = 0;
      groupedItemCount += 1;
    });

    if (groupedItemCount <= 1) return null;

    return {
      groupId: groupId,
      updates: updates,
    };
  }

  function buildAddToCartPayload(form) {
    if (!form) return null;

    const formData = new FormData(form);

    if (formData.get('selling_plan')) return null;

    const addonVariantId = getAddonVariantId(form);
    if (!addonVariantId) return null;

    const mainVariantId = formData.get('id');
    if (!mainVariantId) return null;

    const quantity = parseInt(formData.get('quantity'), 10) || 1;
    const groupId = createGroupId();
    const mainProperties = extractLineItemProperties(formData);

    mainProperties._group_product = groupId;
    mainProperties._group_role = 'main';

    const addonProperties = {
      'Installation Hardware': mainProperties['Installation Hardware'] || '',
      _group_product: groupId,
      _group_role: 'addon',
      _parent_variant_id: String(mainVariantId),
    };

    return {
      groupId: groupId,
      items: [
        {
          id: Number(mainVariantId),
          quantity: quantity,
          properties: mainProperties,
        },
        {
          id: Number(addonVariantId),
          quantity: quantity,
          properties: addonProperties,
        },
      ],
    };
  }

  window.KgGroupedCart = {
    createGroupId: createGroupId,
    extractLineItemProperties: extractLineItemProperties,
    getAddonVariantId: getAddonVariantId,
    hasGroupedAddons: hasGroupedAddons,
    getLineItemProperty: getLineItemProperty,
    getGroupedRemovalUpdates: getGroupedRemovalUpdates,
    findMainCartLineItem: findMainCartLineItem,
    normalizeCartAddResponse: normalizeCartAddResponse,
    buildAddToCartPayload: buildAddToCartPayload,
  };
})();
